/**
 * kanban-task-store.ts - Modularized Kanban State Store
 */

export interface PlannerTask {
  id: string;
  title: string;
  status: 'backlog' | 'in_progress' | 'blocked' | 'review' | 'done';
  taskType?: 'task' | 'action' | 'project' | 'goal' | 'plan';
  description?: string;
  estimateDays?: number;
  progressPercentage?: number;
  updatedAt?: number;
  createdAt?: number;
  fractionalIndex?: string;
}

export interface AppPlannerTask extends PlannerTask {
  assignee?: string;
  blockers?: string[];
  _originKind?: 'demo-fixture' | 'test-fixture' | 'manual';
  [key: string]: unknown;
}

export interface ContextInspectorData {
  type: 'task' | 'proposal' | 'standup' | 'wiki';
  id: string;
  title: string;
  status?: string;
  assignee?: string;
  estimate?: string;
  description?: string;
  blockers?: string[];
  proposedState?: string;
  rationale?: string;
  remedyCmd?: string;
  content?: string;
}

export const SEED_TASK_DETAILS: Record<string, ContextInspectorData> = {};

export function syncInspectorCache(task: AppPlannerTask): void {
  const numericId = task.id.replace(/^task-/, '');
  const data: ContextInspectorData = {
    type: 'task',
    id: task.id,
    title: task.title,
    status: task.status,
    assignee: task.assignee || '@member',
    estimate: `${task.estimateDays || 2} days (${task.progressPercentage || 50}% completed)`,
    description: task.description,
    blockers: task.blockers || [],
  };

  SEED_TASK_DETAILS[task.id] = data;
  SEED_TASK_DETAILS[numericId] = data;
}

const KNOWN_LEGACY_SEED_TASK_IDS = new Set(['task-88', 'task-92', 'task-104', 'task-110']);

function migrateLegacyTasks(tasks: [string, AppPlannerTask][]): [string, AppPlannerTask][] {
  let modified = false;
  const migrated: [string, AppPlannerTask][] = tasks.map(([id, task]) => {
    if (KNOWN_LEGACY_SEED_TASK_IDS.has(task.id) && !task._originKind) {
      modified = true;
      return [id, { ...task, _originKind: 'demo-fixture' }];
    }
    return [id, task];
  });
  return modified ? migrated : tasks;
}

export function getKanbanTasks(room: string = 'scrum'): Map<string, AppPlannerTask> {
  const map = new Map<string, AppPlannerTask>();
  if (typeof window === 'undefined') return map;

  const keysToTry = [`action_planner_tasks_${room}`, `action_planner_tasks_scrum`, `action_planner_tasks_general`];

  for (const key of keysToTry) {
    const saved = localStorage.getItem(key);
    if (saved) {
      try {
        const parsed = JSON.parse(saved) as [string, AppPlannerTask][];
        if (Array.isArray(parsed) && parsed.length > 0) {
          const migrated = migrateLegacyTasks(parsed);
          migrated.forEach(([id, t]) => {
            map.set(id, t);
            syncInspectorCache(t);
          });
          break;
        }
      } catch (err) {
        console.error(`[KanbanTaskStore] Error parsing tasks from ${key}:`, err);
      }
    }
  }

  return map;
}

export function saveKanbanTask(task: AppPlannerTask, room: string = 'scrum'): AppPlannerTask {
  const roomTasks = getKanbanTasks(room);
  roomTasks.set(task.id, task);
  syncInspectorCache(task);

  if (typeof window !== 'undefined') {
    const serialized = JSON.stringify(Array.from(roomTasks.entries()));
    localStorage.setItem(`action_planner_tasks_${room}`, serialized);
    localStorage.setItem(`action_planner_tasks_scrum`, serialized);
    localStorage.setItem(`action_planner_tasks_general`, serialized);

    window.dispatchEvent(new CustomEvent('kanban-tasks-updated', { detail: { room, task } }));
// Emit task schedule event if meshBus is available
publishTaskScheduleEvent({ ...task, priority: (task as any).priority || "normal" }, { workweekDays: 5 }, (globalThis as any).meshBus);
  }

  return task;
}

export function updateKanbanTaskStatus(
  taskId: string,
  newStatus: 'backlog' | 'in_progress' | 'blocked' | 'review' | 'done',
  room: string = 'scrum'
): AppPlannerTask | null {
  const roomTasks = getKanbanTasks(room);
  const existing = roomTasks.get(taskId) || roomTasks.get(`task-${taskId}`);

  if (!existing) return null;

  const updated: AppPlannerTask = {
    ...existing,
    status: newStatus,
    updatedAt: Date.now(),
  };

  return saveKanbanTask(updated, room);
}

export function resetDemoKanbanData(room: string = 'scrum'): void {
  const roomTasks = getKanbanTasks(room);
  let changed = false;

  for (const [id, task] of roomTasks.entries()) {
    if (task._originKind === 'demo-fixture' || KNOWN_LEGACY_SEED_TASK_IDS.has(task.id)) {
      roomTasks.delete(id);
      delete SEED_TASK_DETAILS[task.id];
      delete SEED_TASK_DETAILS[task.id.replace(/^task-/, '')];
      changed = true;
    }
  }

  if (changed && typeof window !== 'undefined') {
    const serialized = JSON.stringify(Array.from(roomTasks.entries()));
    localStorage.setItem(`action_planner_tasks_${room}`, serialized);
    localStorage.setItem(`action_planner_tasks_scrum`, serialized);
    localStorage.setItem(`action_planner_tasks_general`, serialized);
    window.dispatchEvent(new CustomEvent('kanban-tasks-updated', { detail: { room } }));
  }
}

/**
 * Publish task schedule event to mesh bus.
 */
export async function publishTaskScheduleEvent(
  task: {
    id: string;
    title: string;
    dueDate?: string;
    priority: string;
    status: string;
  },
  settings: { workweekDays: number },
  meshBus?: { emit: (channel: string, event: any) => void }
) {
  if (!task.dueDate || !meshBus) return;
  await meshBus.emit('mesh:events', {
    type: 'task:scheduled',
    payload: {
      taskId: task.id,
      title: task.title,
      dueDate: task.dueDate,
      workweekDays: settings.workweekDays,
      priority: task.priority,
      status: task.status,
    },
  });
}

export const kanbanTaskStore = {
  getTasks: getKanbanTasks,
  saveTask: saveKanbanTask,
  updateStatus: updateKanbanTaskStatus,
  resetDemoKanbanData,
  injectTask: (task: AppPlannerTask, room: string = 'scrum') => saveKanbanTask(task, room),
};

export function subscribeToKanbanTasks(callback: () => void): () => void {
  if (typeof window === 'undefined') return () => {};

  const handler = () => callback();
  window.addEventListener('kanban-tasks-updated', handler);
  window.addEventListener('storage', handler);

  return () => {
    window.removeEventListener('kanban-tasks-updated', handler);
    window.removeEventListener('storage', handler);
  };
}
